/**
 * SlideOverPanel — generic slide-in panel from the right (or another edge),
 * wrapping the `Sheet` primitive with the header/body/footer shape used
 * across the app's "quick view" panels (see the job quick-view panel for
 * the pattern this generalises).
 *
 * Give it a title and whatever "screen" you want shown — a read-only
 * summary, a form, a list of filters — as `children`. The panel owns the
 * slide-in mechanics, the close button clearance, and the scrollable body;
 * callers only own their own content.
 *
 * Not suitable for content that needs its own route (use a page) or for
 * transient confirmations (use a `Dialog`).
 */

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "../primitives/sheet";
import { cn } from "../primitives/utils";

export interface SlideOverPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Panel heading. Required. */
  title: React.ReactNode;
  /** Optional secondary text under the title. */
  description?: React.ReactNode;
  /** Optional sticky footer, typically action buttons. */
  footer?: React.ReactNode;
  /** The screen to display in the panel body. */
  children: React.ReactNode;
  /** Edge the panel slides in from. Defaults to "right". */
  side?: "top" | "right" | "bottom" | "left";
  /** Extra classes for the panel's width/sizing. */
  className?: string;
}

export function SlideOverPanel({
  open,
  onOpenChange,
  title,
  description,
  footer,
  children,
  side = "right",
  className,
}: SlideOverPanelProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={side} className={cn("p-0 flex flex-col", className)}>
        <SheetHeader className="border-b border-border pb-4 pr-10">
          <SheetTitle className="text-base">{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer && (
          <SheetFooter className="border-t border-border">{footer}</SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
